// Right-click (desktop) / long-press (touch) menu for a grid cell.
//
// Positioning follows the same clamp/flip algorithm as the header filter menu in
// `Grid.tsx`, except it is anchored to a POINT (the pointer) instead of a trigger
// rect: place at the point, flip left/up when it would overflow, then clamp into
// the viewport. There is no shared popover utility in the repo, so the logic
// lives here rather than being extracted out from under the filter menu.
//
// On narrow screens the same items render as a bottom sheet, mirroring the
// desktop-portal + `sm:hidden` sheet pairing the filter menu already uses.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useT } from '@/shared/i18n'

/** Viewport padding kept free on every side, matching the filter menu. */
const PAD = 8
const MENU_WIDTH = 232

export type GridMenuItem =
  | { kind: 'action'; key: string; icon?: ReactNode; label: string; disabled?: boolean; onSelect: () => void; /** Shown at the right, e.g. "Ctrl+'". */ shortcut?: string }
  | { kind: 'submenu'; key: string; icon?: ReactNode; label: string; items: GridMenuItem[] }
  /** Hosts a self-contained control (the shared `ColorPicker`). Never auto-closes the menu. */
  | { kind: 'node'; key: string; node: ReactNode }
  | { kind: 'separator'; key: string }

export interface GridContextMenuProps {
  /** Pointer position the menu is anchored to, in client coordinates. */
  x: number
  y: number
  items: GridMenuItem[]
  onClose: () => void
  /** Accessible name for the menu. */
  label: string
  /** Opened by a long press. Touch then emits compatibility mouse events on release,
   *  which would otherwise read as a click outside and close the menu immediately. */
  openedByTouch?: boolean
}

const ITEM_CLASS =
  'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm sm:py-1.5 sm:text-xs ' +
  'text-text transition-colors hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-40 ' +
  'disabled:hover:bg-transparent focus-visible:bg-surface-3 focus-visible:outline-none'

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`ml-auto shrink-0 text-text-muted transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

/** A submenu that opens beside the menu, portalled so the parent's scroll box never clips it. */
function FlyoutSubmenu({
  item,
  onClose,
  open,
  setExpanded,
}: {
  item: Extract<GridMenuItem, { kind: 'submenu' }>
  onClose: () => void
  open: boolean
  setExpanded: (key: string | null) => void
}) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  // Anchored to the trigger: to its right, flipped left when that would overflow,
  // then clamped — the same rule the menu itself follows against the pointer.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const height = panelRef.current?.offsetHeight ?? item.items.length * 34 + 8
      let left = rect.right - 2
      if (left + MENU_WIDTH > window.innerWidth - PAD) left = rect.left - MENU_WIDTH + 2
      left = Math.max(PAD, Math.min(left, window.innerWidth - MENU_WIDTH - PAD))
      const top = Math.max(PAD, Math.min(rect.top - 4, window.innerHeight - height - PAD))
      setPosition({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, item.items.length])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseEnter={() => setExpanded(item.key)}
        onClick={() => setExpanded(open ? null : item.key)}
        className={`${ITEM_CLASS} tap-target sm:min-h-0`}
      >
        {item.icon}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        <Chevron open={false} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          data-grid-context-menu="1"
          role="menu"
          aria-label={item.label}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'fixed',
            top: position?.top ?? -9999,
            left: position?.left ?? -9999,
            width: MENU_WIDTH,
            zIndex: 51,
          }}
          className="hidden max-h-[80vh] flex-col overflow-y-auto rounded-lg border border-border bg-surface py-1 text-text shadow-card sm:flex"
        >
          <MenuItems items={item.items} onClose={onClose} expanded={null} setExpanded={setExpanded} depth={1} />
        </div>,
        document.body,
      )}
    </>
  )
}

/** Renders one level of items. On the desktop menu a submenu opens as a flyout beside
 *  the row, on hover or tap; the mobile sheet has no room for that, so there it expands
 *  in place. Hovering any other top-level row closes whatever flyout is open. */
function MenuItems({
  items,
  onClose,
  expanded,
  setExpanded,
  depth = 0,
  flyout = false,
}: {
  items: GridMenuItem[]
  onClose: () => void
  expanded: string | null
  setExpanded: (key: string | null) => void
  depth?: number
  /** Desktop: submenus fly out to the side instead of expanding in place. */
  flyout?: boolean
}) {
  return (
    <>
      {items.map((item) => {
        if (item.kind === 'separator') {
          return <div key={item.key} role="separator" className="my-1 h-px bg-border" />
        }
        if (item.kind === 'node') {
          return (
            <div key={item.key} className={depth > 0 ? 'pl-4' : undefined}>
              {item.node}
            </div>
          )
        }
        if (item.kind === 'submenu') {
          const open = expanded === item.key
          if (flyout) {
            return (
              <FlyoutSubmenu
                key={item.key}
                item={item}
                onClose={onClose}
                open={open}
                setExpanded={setExpanded}
              />
            )
          }
          return (
            <div key={item.key}>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={open}
                onMouseEnter={depth === 0 ? () => setExpanded(item.key) : undefined}
                onClick={() => setExpanded(open ? null : item.key)}
                className={`${ITEM_CLASS} tap-target sm:min-h-0 ${depth > 0 ? 'pl-7' : ''}`}
              >
                {item.icon}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <Chevron open={open} />
              </button>
              {open && (
                <MenuItems
                  items={item.items}
                  onClose={onClose}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  depth={depth + 1}
                />
              )}
            </div>
          )
        }
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onMouseEnter={depth === 0 ? () => setExpanded(null) : undefined}
            onClick={() => {
              item.onSelect()
              onClose()
            }}
            className={`${ITEM_CLASS} tap-target sm:min-h-0 ${depth > 0 ? 'pl-7' : ''}`}
          >
            {item.icon}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {item.shortcut && <span className="shrink-0 pl-3 text-text-muted">{item.shortcut}</span>}
          </button>
        )
      })}
    </>
  )
}

export default function GridContextMenu({ x, y, items, onClose, label, openedByTouch }: GridContextMenuProps) {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: y, left: x })
  const [expanded, setExpanded] = useState<string | null>(null)

  // Reposition once the panel has been measured, and again on resize/scroll so
  // the menu never detaches from the viewport while it is open.
  useLayoutEffect(() => {
    const updatePosition = () => {
      const height = panelRef.current?.offsetHeight ?? 0
      const width = panelRef.current?.offsetWidth ?? MENU_WIDTH

      let left = x
      if (left + width > window.innerWidth - PAD) left = x - width
      left = Math.max(PAD, Math.min(left, window.innerWidth - width - PAD))

      let top = y
      if (top + height > window.innerHeight - PAD && y - height >= PAD) top = y - height
      else if (top + height > window.innerHeight - PAD) top = Math.max(PAD, window.innerHeight - height - PAD)

      setPosition({ top, left })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
    // `expanded` is a dependency because expanding a submenu changes the height.
  }, [x, y, expanded])

  const isInside = useCallback((target: Node) => {
    if (panelRef.current?.contains(target) || sheetRef.current?.contains(target)) return true
    // Submenu flyouts are portalled separately; they carry the same marker attribute.
    const element = target instanceof Element ? target : (target as Node).parentElement
    return Boolean(element?.closest('[data-grid-context-menu="1"]'))
  }, [])

  useEffect(() => {
    // Only `mousedown` closes the menu. Listening to `contextmenu` as well would break
    // right-clicking straight from one cell to another: React's handler opens the menu
    // at the new cell first, and the document-level listener would then close it again.
    // A right-click always emits `mousedown` too, so nothing is lost.
    //
    // `ColorPicker` renders its palette inside its own container rather than a separate
    // portal, so a containment check is enough to keep the menu open while picking.
    const openedAt = Date.now()
    const onOutside = (e: MouseEvent) => {
      if (openedByTouch && Date.now() - openedAt < 600) return
      if (!isInside(e.target as Node)) onClose()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    // Capture phase, not bubble: the grid's own cell `onMouseDown` calls
    // `stopPropagation()`, so a bubbling listener on `document` never sees a click
    // that lands on another cell — the menu would stay open over the whole grid.
    document.addEventListener('mousedown', onOutside, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onOutside, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isInside, onClose, openedByTouch])

  if (items.length === 0) return null

  return (
    <>
      {/* Desktop floating menu */}
      {createPortal(
        <div
          ref={panelRef}
          data-grid-context-menu="1"
          role="menu"
          aria-label={label}
          onContextMenu={(e) => e.preventDefault()}
          style={{ position: 'fixed', top: position.top, left: position.left, width: MENU_WIDTH, zIndex: 50 }}
          className="hidden max-h-[80vh] flex-col overflow-y-auto rounded-lg border border-border bg-surface py-1 text-text shadow-card sm:flex"
        >
          <MenuItems items={items} onClose={onClose} expanded={expanded} setExpanded={setExpanded} flyout />
        </div>,
        document.body,
      )}

      {/* Mobile bottom sheet */}
      {createPortal(
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-[var(--overlay)] sm:hidden">
          <button type="button" aria-label={t('common.close')} onClick={onClose} className="absolute inset-0" />
          <div
            ref={sheetRef}
            data-grid-context-menu="1"
            role="menu"
            aria-label={label}
            className="relative max-h-[75vh] overflow-y-auto rounded-t-token-lg border-t border-border bg-surface pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 text-text shadow-card"
          >
            <MenuItems items={items} onClose={onClose} expanded={expanded} setExpanded={setExpanded} />
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
